import React, { useEffect, useMemo, useState } from 'react';
import './jackpotStrip.css';
import { formatUSD } from '../../utils/money';

const NAMES = ['Jayden', 'Maria', 'Chris', 'Alex', 'Sam', 'Taylor', 'Jordan', 'Casey', 'Riley', 'Morgan'];
const TIMES = ['just now', '1m ago', '2m ago', '4m ago', '6m ago', '9m ago'];

const maskName = (name) => `${name.slice(0, 3)}***`;

// Banner above Top Games: today's payout pool (ticking up) + rotating recent wins.
// Amounts reuse formatUSD so units match the cards (integer coins -> dollars).
export default function JackpotStrip({ games = [], surveys = [] }) {
  const wins = useMemo(() => {
    const gameWins = (games || []).slice(0, 8).map((g, i) => ({
      key: `g-${g.id ?? i}`,
      user: maskName(NAMES[(i * 3 + 1) % NAMES.length]),
      amount: g.reward,
      title: g.title,
      time: TIMES[i % TIMES.length],
    }));
    const surveyWins = (surveys || []).slice(0, 4).map((s, i) => ({
      key: `s-${s.id ?? i}`,
      user: maskName(NAMES[(i * 5 + 2) % NAMES.length]),
      amount: s.payout,
      title: 'CPX Research Survey',
      time: TIMES[(i + 2) % TIMES.length],
    }));
    const mixed = [];
    const max = Math.max(gameWins.length, surveyWins.length);
    for (let i = 0; i < max; i++) {
      if (gameWins[i]) mixed.push(gameWins[i]);
      if (surveyWins[i]) mixed.push(surveyWins[i]);
    }
    return mixed;
  }, [games, surveys]);

  const basePool = useMemo(() => {
    const gameTotal = (games || []).reduce((sum, g) => sum + (Number(g.reward) || 0), 0);
    const surveyTotal = (surveys || []).reduce((sum, s) => sum + (Number(s.payout) || 0), 0);
    return gameTotal + surveyTotal;
  }, [games, surveys]);

  const [pool, setPool] = useState(0);
  const [drips, setDrips] = useState(0);
  const [winIndex, setWinIndex] = useState(0);
  const reduceMotion =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Count-up when the pool first loads.
  useEffect(() => {
    if (!basePool) return;
    if (reduceMotion) {
      setPool(basePool);
      return;
    }
    let raf;
    const start = performance.now();
    const dur = 1200;
    const tick = (now) => {
      const p = Math.min(1, (now - start) / dur);
      setPool(Math.round(basePool * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [basePool, reduceMotion]);

  // Pool creeps up every few seconds so it feels live (13 coins = $0.13/tick).
  useEffect(() => {
    if (reduceMotion || !basePool) return undefined;
    const id = setInterval(() => setDrips((d) => d + 1), 4000);
    return () => clearInterval(id);
  }, [basePool, reduceMotion]);

  // Rotate the winners ticker.
  useEffect(() => {
    if (reduceMotion || wins.length < 2) return undefined;
    const id = setInterval(() => setWinIndex((i) => (i + 1) % wins.length), 4000);
    return () => clearInterval(id);
  }, [wins.length, reduceMotion]);

  const current = wins.length ? wins[winIndex % wins.length] : null;

  return (
    <div className="jackpot-strip">
      <div className="jackpot-top">
        <div>
          <div className="jackpot-label">
            <span aria-hidden="true">💰</span> Your payout pool
          </div>
          <div className="jackpot-amount">{formatUSD(pool + drips * 13)}</div>
        </div>
        <div className="jackpot-live" aria-hidden="true">
          <span className="live-dot" /> Live
        </div>
      </div>
      <div className="jackpot-ticker" aria-hidden="true">
        {current ? (
          <div key={`${current.key}-${winIndex}`} className="ticker-item">
            <span className="ticker-user">{current.user}</span>
            {' '}just earned <span className="ticker-amount">{formatUSD(current.amount)}</span>
            {' — '}{current.title} <span className="ticker-time">• {current.time}</span>
          </div>
        ) : (
          <div className="ticker-item ticker-loading">Loading today&apos;s winners…</div>
        )}
      </div>
    </div>
  );
}
